// client/src/components/pages/OperationOverview/exportOverviewPDF.ts (converted from .js, ST-00216)
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import type { CellHookData, UserOptions } from 'jspdf-autotable';
import { formatMiningDate, formatMiningDateTime } from '../../../utils/dateUtils';
import { adjustedDispatch, adjustedDispatchCount } from './overviewKpis';
import { routeVariance } from './routeSort';
import type {
  FallbackNote,
  OverviewGrouping,
  OverviewKpis,
} from '../../../types/clientPages/operationOverview';

/**
 * The `jspdf-autotable` side-effect import adds `doc.autoTable(...)`, and jsPDF's
 * `internal.getNumberOfPages()` exists at runtime; neither is in jsPDF's types.
 */
type AutoTableDoc = jsPDF & {
  autoTable: (options: UserOptions) => void;
  internal: jsPDF['internal'] & { getNumberOfPages: () => number };
};

export interface ExportOverviewPdfInput {
  groupings: OverviewGrouping[];
  summaryStats: OverviewKpis;
  startDate: Date;
  endDate: Date;
  /** ST-00218 P3: the page's fallback note (a "Truck + time" route shows delivery-number figures). */
  matchingNote?: FallbackNote | null;
}

// Whole tonnes, like the tiles and cards on screen (it printed "1,581,672.308 MT").
const tonnes = (value: number | null | undefined): string =>
  Math.round(value || 0).toLocaleString();

const exportOverviewPDF = ({
  groupings,
  summaryStats,
  startDate,
  endDate,
  matchingNote = null,
}: ExportOverviewPdfInput): void => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }) as AutoTableDoc;
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(18);
  doc.setTextColor(45, 49, 66);
  doc.text('Operations Overview', 14, 18);

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    `Period: ${formatMiningDate(startDate)} – ${formatMiningDate(endDate)}  |  Generated: ${formatMiningDateTime(new Date().toISOString())}`,
    14,
    25
  );
  if (matchingNote) {
    doc.setFontSize(8);
    doc.setTextColor(146, 64, 14);
    doc.text(
      `${matchingNote.note}.${matchingNote.detail ? ` ${matchingNote.detail}` : ''}`,
      14,
      29.5
    );
  }

  const kpis = [
    { label: 'Routes', value: String(summaryStats.totalRoutes) },
    { label: 'Dispatched', value: `${tonnes(summaryStats.totalDispatched)} MT` },
    { label: 'Received', value: `${tonnes(summaryStats.totalReceived)} MT` },
    { label: 'Total Loads', value: summaryStats.totalLoads.toLocaleString() },
    {
      // ST-00298: n/a, or leaving out, the routes received against nothing left dispatched.
      label: summaryStats.varianceExcludedRoutes
        ? `Avg Variance (excl. ${summaryStats.varianceExcludedRoutes} n/a)`
        : 'Avg Variance',
      value: summaryStats.avgVariance === null ? 'n/a' : `${summaryStats.avgVariance.toFixed(2)}%`,
    },
  ];

  const kpiWidth = (pageWidth - 28) / kpis.length;
  const kpiY = 32;

  kpis.forEach((kpi, i) => {
    const x = 14 + i * kpiWidth;
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(x, kpiY, kpiWidth - 3, 14, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(kpi.label.toUpperCase(), x + 3, kpiY + 5);
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(kpi.value, x + 3, kpiY + 11);
  });

  const headers = [
    'Route',
    'Dispatched (MT)',
    'Received (MT)',
    'Loads Out',
    'Loads In',
    'Difference (MT)',
    'Variance %',
  ];

  const body = groupings.map((g) => {
    // The server's figures (ST-00218 P3) when sent, else dispatched + BF − CF; n/a = no variance.
    const adjDispatch = adjustedDispatch(g);
    const diff = (g.totalReceiptWeight || 0) - adjDispatch;
    const variance = routeVariance(g);
    return [
      g.name,
      tonnes(adjDispatch),
      tonnes(g.totalReceiptWeight),
      String(adjustedDispatchCount(g)),
      String(g.totalReceiptCount || 0),
      `${diff > 0 ? '+' : ''}${Math.round(diff).toLocaleString()}`,
      variance === null ? 'n/a' : `${variance >= 0 ? '+' : ''}${variance.toFixed(2)}%`,
    ];
  });

  doc.autoTable({
    startY: kpiY + 20,
    head: [headers],
    body,
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: {
      fillColor: [69, 183, 170],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
    },
    columnStyles: { 0: { cellWidth: 50 } },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (data: CellHookData) => {
      if (data.section === 'body' && data.column.index === 6) {
        // The body cells are the strings built above.
        if (data.cell.raw === 'n/a') {
          data.cell.styles.textColor = [100, 116, 139];
          return;
        }
        const val = parseFloat(data.cell.raw as string);
        if (Math.abs(val) > 5) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        } else if (Math.abs(val) > 3) {
          data.cell.styles.textColor = [217, 119, 6];
        } else {
          data.cell.styles.textColor = [16, 185, 129];
        }
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.text(
      `LexiBridge Operations Dashboard  |  Page ${i} of ${pageCount}`,
      14,
      doc.internal.pageSize.getHeight() - 8
    );
  }

  doc.save(
    `Operations-Overview-${formatMiningDate(startDate)}-to-${formatMiningDate(endDate)}.pdf`
  );
};

export default exportOverviewPDF;
